import { Suspense, lazy } from "react";
import { Route } from "react-router";
import { Routes } from "@datadog/browser-rum-react/react-router-v7";

import Home from "pages/home";
import PageNotFound from "pages/404";
import Loader from "core/components/Loader";
import {
  IsAuthLoading,
  Unauthenticated,
  Authenticated,
  HandleAuthError,
  HandleSessionExpiry,
} from "core/auth";
import {
  ApplicationErrorBoundary,
  AuthErrorBoundary,
  PageErrorBoundary,
} from "core/boundaries";
import ActiveMembershipProvider from "core/context/ActiveMembershipProvider";
import CurrentUserProvider from "core/context/CurrentUserProvider";
import FeatureProvider from "core/context/FeatureProvider";
import LazyAppRoute from "core/components/LazyAppRoute";
import Navigate from "core/components/Navigate";

import MemberDetailsPanel from "modules/admin/MemberDetailsPanel";
import AddMemberPanel from "modules/admin/AddMemberPanel";
import CreateUserPanel from "modules/admin/CreateUserPanel";
import EditUserPanel from "modules/admin/EditUserPanel";
import OrganizationLayout from "modules/admin/OrganizationLayout";
import OrganizationSegmentSwitcherPage from "pages/organization/segment-switcher";

import OrganizationGeneralPage from "pages/organization/general";
import OrganizationMemberLicensesPage from "pages/organization/member-licenses";
import OrganizationMembersPage from "pages/organization/members";
import OrganizationCredentialsPage from "pages/organization/credentials";
import OrganizationIntegrationsPage from "pages/organization/integrations";
import OrganizationPreferencesPage from "pages/organization/preferences";

import ProductLicenseFormProvider from "core/context/ProductLicenseFormProvider";
import ProductLicenseSummaryPage from "pages/organization/product-license-summary";
import ProductLicenseConfigurePage from "pages/organization/product-license-configure";
import ProductLicenseConfirmPage from "pages/organization/product-license-confirm";
import ProductLicenseProductPage from "pages/organization/product-license-product";
import CreateOrganizationPanel from "modules/admin/CreateOrganizationPanel";
import UserProfileLayout from "pages/admin/user-profile/layout";
import UserProfileGeneralPage from "pages/admin/user-profile/general";
import UserProfileChangePasswordPage from "pages/admin/user-profile/change-password";
import UserProfileMembershipsPage from "pages/admin/user-profile/memberships";
import UserProfileCredentialsPage from "pages/admin/user-profile/credentials";
import { UnauthorizedError } from "core/error";
import AppLayout from "core/components/AppLayout";

const ManagedProviders = lazy(() => import("pages/config/managed-providers"));
const Aliases = lazy(() => import("pages/config/aliases"));
const Relationships = lazy(() => import("pages/config/relationships"));
const UpdateRelationships = lazy(() =>
  import("pages/config/update-relationships")
);
const RemoveRelationships = lazy(() =>
  import("pages/config/remove-relationships")
);
const UpdateAliases = lazy(() => import("pages/config/update-aliases"));
const RemoveAliases = lazy(() => import("pages/config/remove-aliases"));
const OrganizationManagement = lazy(() => import("pages/admin/org-management"));
const UserManagement = lazy(() => import("pages/admin/user-management"));
const Analytics = lazy(() => import("pages/analytics"));
const AnalyticsHub = lazy(() => import("pages/analytics-hub"));
const OrganizationAnalyticsHubPage = lazy(() =>
  import("pages/organization/analytics-hub")
);

const App = () => {
  return (
    <AuthErrorBoundary>
      <HandleAuthError />
      <IsAuthLoading>
        <Loader
          style={{
            height: "100dvh",
            paddingBottom: "20vh",
          }}
        />
      </IsAuthLoading>

      <Unauthenticated>
        <Loader
          style={{
            height: "100dvh",
            paddingBottom: "20vh",
          }}
        />
      </Unauthenticated>

      <Authenticated>
        <ApplicationErrorBoundary>
          <HandleSessionExpiry>
            <CurrentUserProvider>
              <ActiveMembershipProvider>
                <FeatureProvider>
                  <Routes>
                    {/* Home Route */}
                    <Route
                      index
                      element={
                        <AppLayout pageTitle="Home" hideAppShell>
                          <Home />
                        </AppLayout>
                      }
                    />

                    {/* Analytics Hub Route */}
                    <Route
                      path="analytics-hub"
                      element={
                        <LazyAppRoute pageTitle="Analytics Hub">
                          <AnalyticsHub />
                        </LazyAppRoute>
                      }
                    />

                    {/* User Profile Route */}
                    <Route
                      path="user-profile/:userId"
                      element={
                        <LazyAppRoute>
                          <UserProfileLayout />
                        </LazyAppRoute>
                      }
                    >
                      <Route index element={<Navigate to="general" />} />
                      <Route
                        path="general"
                        element={<UserProfileGeneralPage />}
                      />

                      <Route
                        path="change-password"
                        element={<UserProfileChangePasswordPage />}
                      />
                      <Route
                        path="memberships"
                        element={<UserProfileMembershipsPage />}
                      />
                      <Route
                        path="credentials"
                        element={<UserProfileCredentialsPage />}
                      />
                    </Route>

                    {/* Organization Management Routes */}
                    <Route
                      path="organizations"
                      element={
                        <LazyAppRoute>
                          <OrganizationManagement />
                        </LazyAppRoute>
                      }
                    >
                      <Route
                        path="create"
                        element={<CreateOrganizationPanel />}
                      />
                    </Route>

                    {/* Organization Details Route */}
                    <Route
                      path="organization/:orgId"
                      element={
                        <LazyAppRoute>
                          <OrganizationLayout />
                        </LazyAppRoute>
                      }
                    >
                      <Route index element={<Navigate to="general" />} />
                      <Route path="general">
                        <Route index element={<OrganizationGeneralPage />} />
                        <Route
                          path="segment-switcher"
                          element={<OrganizationSegmentSwitcherPage />}
                        />
                      </Route>

                      <Route
                        path="members"
                        element={<OrganizationMembersPage />}
                      >
                        <Route
                          path=":userId"
                          element={<MemberDetailsPanel />}
                        />
                        <Route path="add-member" element={<AddMemberPanel />} />
                        <Route
                          path="create-user"
                          element={<CreateUserPanel />}
                        />
                        <Route
                          path="update/:userId"
                          element={<EditUserPanel />}
                        />
                      </Route>

                      <Route
                        path="member-licenses"
                        element={<OrganizationMemberLicensesPage />}
                      />
                      <Route
                        path="product"
                        element={<ProductLicenseFormProvider />}
                      >
                        <Route index element={<Navigate to="summary" />} />
                        <Route
                          path="summary"
                          element={<ProductLicenseSummaryPage />}
                        />
                        <Route
                          path="configure"
                          element={<ProductLicenseConfigurePage />}
                        />
                        <Route
                          path="confirm"
                          element={<ProductLicenseConfirmPage />}
                        />
                        <Route
                          path=":bundlePath/:productPath"
                          element={<ProductLicenseProductPage />}
                        />
                      </Route>

                      <Route
                        path="analytics-hub"
                        element={
                          <Suspense
                            fallback={
                              <Loader style={{ paddingBottom: "20vh" }} />
                            }
                          >
                            <OrganizationAnalyticsHubPage />
                          </Suspense>
                        }
                      />

                      {/* Data Context — configurations with aliases and relationships */}
                      <Route
                        path="configurations"
                        element={
                          <Suspense
                            fallback={
                              <Loader style={{ paddingBottom: "20vh" }} />
                            }
                          >
                            <ManagedProviders />
                          </Suspense>
                        }
                      >
                        <Route index element={<Navigate to="aliases" />} />
                        <Route
                          path="aliases"
                          element={
                            <Suspense
                              fallback={
                                <Loader style={{ paddingBottom: "20vh" }} />
                              }
                            >
                              <Aliases />
                            </Suspense>
                          }
                        />
                        <Route
                          path="relationships"
                          element={
                            <Suspense
                              fallback={
                                <Loader style={{ paddingBottom: "20vh" }} />
                              }
                            >
                              <Relationships />
                            </Suspense>
                          }
                        />
                      </Route>
                      <Route
                        path="configurations/aliases/update"
                        element={
                          <Suspense
                            fallback={
                              <Loader style={{ paddingBottom: "20vh" }} />
                            }
                          >
                            <UpdateAliases />
                          </Suspense>
                        }
                      />
                      <Route
                        path="configurations/aliases/update/:npi"
                        element={
                          <Suspense
                            fallback={
                              <Loader style={{ paddingBottom: "20vh" }} />
                            }
                          >
                            <UpdateAliases />
                          </Suspense>
                        }
                      />
                      <Route
                        path="configurations/aliases/remove"
                        element={
                          <Suspense
                            fallback={
                              <Loader style={{ paddingBottom: "20vh" }} />
                            }
                          >
                            <RemoveAliases />
                          </Suspense>
                        }
                      />
                      <Route
                        path="configurations/aliases/remove/:npi"
                        element={
                          <Suspense
                            fallback={
                              <Loader style={{ paddingBottom: "20vh" }} />
                            }
                          >
                            <RemoveAliases />
                          </Suspense>
                        }
                      />
                      <Route
                        path="configurations/relationships/update"
                        element={
                          <Suspense
                            fallback={
                              <Loader style={{ paddingBottom: "20vh" }} />
                            }
                          >
                            <UpdateRelationships />
                          </Suspense>
                        }
                      />
                      <Route
                        path="configurations/relationships/update/:npi"
                        element={
                          <Suspense
                            fallback={
                              <Loader style={{ paddingBottom: "20vh" }} />
                            }
                          >
                            <UpdateRelationships />
                          </Suspense>
                        }
                      />
                      <Route
                        path="configurations/relationships/remove"
                        element={
                          <Suspense
                            fallback={
                              <Loader style={{ paddingBottom: "20vh" }} />
                            }
                          >
                            <RemoveRelationships />
                          </Suspense>
                        }
                      />
                      <Route
                        path="configurations/relationships/remove/:npi"
                        element={
                          <Suspense
                            fallback={
                              <Loader style={{ paddingBottom: "20vh" }} />
                            }
                          >
                            <RemoveRelationships />
                          </Suspense>
                        }
                      />

                      <Route
                        path="credentials"
                        element={<OrganizationCredentialsPage />}
                      />
                      <Route
                        path="integrations"
                        element={<OrganizationIntegrationsPage />}
                      />
                      <Route
                        path="preferences"
                        element={<OrganizationPreferencesPage />}
                      />
                    </Route>

                    {/* Configuration View Routes */}
                    <Route
                      path="configurations/:orgId"
                      element={
                        <LazyAppRoute pageTitle="Managed Providers">
                          <ManagedProviders />
                        </LazyAppRoute>
                      }
                    >
                      <Route index element={<Navigate to="aliases" />} />

                      <Route path="aliases">
                        <Route
                          index
                          element={
                            <Suspense
                              fallback={
                                <Loader style={{ paddingBottom: "20vh" }} />
                              }
                            >
                              <Aliases />
                            </Suspense>
                          }
                        />
                      </Route>

                      <Route path="relationships">
                        <Route
                          index
                          element={
                            <Suspense
                              fallback={
                                <Loader style={{ paddingBottom: "20vh" }} />
                              }
                            >
                              <Relationships />
                            </Suspense>
                          }
                        />
                      </Route>
                    </Route>

                    {/* Configuration Add/Update/Remove Routes */}
                    <Route
                      path="configurations/:orgId/aliases/update"
                      element={
                        <LazyAppRoute pageTitle="Update Aliases">
                          <UpdateAliases />
                        </LazyAppRoute>
                      }
                    />
                    <Route
                      path="configurations/:orgId/aliases/update/:npi"
                      element={
                        <LazyAppRoute pageTitle="Update Aliases">
                          <UpdateAliases />
                        </LazyAppRoute>
                      }
                    />
                    <Route
                      path="configurations/:orgId/aliases/remove"
                      element={
                        <LazyAppRoute
                          fallback={
                            <Loader style={{ paddingBottom: "20vh" }} />
                          }
                        >
                          <RemoveAliases />
                        </LazyAppRoute>
                      }
                    />
                    <Route
                      path="configurations/:orgId/aliases/remove/:npi"
                      element={
                        <LazyAppRoute pageTitle="Remove Aliases">
                          <RemoveAliases />
                        </LazyAppRoute>
                      }
                    />
                    <Route
                      path="configurations/:orgId/relationships/update"
                      element={
                        <LazyAppRoute pageTitle="Update Relationships">
                          <UpdateRelationships />
                        </LazyAppRoute>
                      }
                    />
                    <Route
                      path="configurations/:orgId/relationships/update/:npi"
                      element={
                        <LazyAppRoute pageTitle="Update Relationships">
                          <UpdateRelationships />
                        </LazyAppRoute>
                      }
                    />
                    <Route
                      path="configurations/:orgId/relationships/remove"
                      element={
                        <LazyAppRoute pageTitle="Remove Relationships">
                          <RemoveRelationships />
                        </LazyAppRoute>
                      }
                    />
                    <Route
                      path="configurations/:orgId/relationships/remove/:npi"
                      element={
                        <LazyAppRoute pageTitle="Remove Relationships">
                          <RemoveRelationships />
                        </LazyAppRoute>
                      }
                    />

                    {/* All Users Routes */}
                    <Route
                      path="users"
                      element={
                        <LazyAppRoute>
                          <UserManagement />
                        </LazyAppRoute>
                      }
                    >
                      <Route path="create" element={<CreateUserPanel />} />
                      <Route path=":userId" element={<EditUserPanel />} />
                    </Route>

                    {/* Analytics Routes */}
                    <Route path="analytics">
                      <Route
                        index
                        path="*"
                        element={
                          <LazyAppRoute>
                            <Analytics />
                          </LazyAppRoute>
                        }
                      />
                    </Route>

                    <Route
                      path="404"
                      element={
                        <AppLayout hideAppShell={true}>
                          <PageNotFound />
                        </AppLayout>
                      }
                    />
                    <Route
                      path="unauthorized"
                      element={
                        <AppLayout hideAppShell={true}>
                          <UnauthorizedError />
                        </AppLayout>
                      }
                    />
                    <Route path="*" element={<Navigate to="/404" replace />} />
                  </Routes>
                </FeatureProvider>
              </ActiveMembershipProvider>
            </CurrentUserProvider>
          </HandleSessionExpiry>
        </ApplicationErrorBoundary>
      </Authenticated>
    </AuthErrorBoundary>
  );
};

export default App;
